import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import type { Organization } from "../types/organization.types";

type BackendOrganization = {
	address?: null | string;
	code: string;
	completionYear?: null | string;
	createdAt: string;
	directorName?: null | string;
	id: string;
	inn?: null | string;
	accountNumber?: null | string;
	bankMfo?: null | string;
	oked?: null | string;
	isActive: boolean;
	name: string;
	phone?: null | string;
};

type OrganizationsListResponse = {
	items: BackendOrganization[];
	meta: {
		limit: number;
		page: number;
		total: number;
		totalPages: number;
	};
};

type SuccessEnvelope<T> = {
	data: T;
	success: boolean;
};

export type OrganizationSavePayload = {
	address?: string;
	code: string;
	completionYear?: string;
	directorName?: string;
	inn?: string;
	accountNumber?: string;
	bankMfo?: string;
	oked?: string;
	name: string;
	phone?: string;
	admin?: {
		firstName: string;
		lastName: string;
		phone: string;
		password?: string;
	};
};

export type OrganizationsListParams = {
	isActive?: "false" | "true";
	search?: string;
	limit?: number;
	page?: number;
};

const getInitials = (name: string) =>
	name
		.split(" ")
		.map((part) => part[0] || "")
		.join("")
		.slice(0, 2)
		.toUpperCase();

export const formatOrganizationDate = (value: string) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return new Intl.DateTimeFormat("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
		hour12: false,
	}).format(date);
};

const mapOrganization = (organization: BackendOrganization): Organization => ({
	id: organization.id,
	name: organization.name,
	code: organization.code,
	phone: organization.phone ?? "",
	createdAt: formatOrganizationDate(organization.createdAt),
	address: organization.address,
	directorName: organization.directorName,
	inn: organization.inn,
	accountNumber: organization.accountNumber,
	bankMfo: organization.bankMfo,
	oked: organization.oked,
	completionYear: organization.completionYear,
	isActive: organization.isActive,
	status: organization.isActive ? "active" : "disabled",
	tone: "blue",
	initials: getInitials(organization.name),
	buildingsCount: 0,
	activeSalesCount: 0,
	clientsCount: 0,
	totalRoomsCount: 0,
	soldRoomsCount: 0,
	availableRoomsCount: 0,
	totalRevenue: "0",
	expectedRevenue: "0",
	usersCount: 0,
});

const unwrapSuccessEnvelope = <T>(payload: SuccessEnvelope<T> | T): T => {
	if (payload && typeof payload === "object" && "success" in payload && "data" in payload) {
		return (payload as SuccessEnvelope<T>).data;
	}

	return payload as T;
};

export const organizationsApi = {
	getOrganizations: async (params: OrganizationsListParams = {}): Promise<Organization[]> => {
		const response = await apiClient.get<
			OrganizationsListResponse | SuccessEnvelope<OrganizationsListResponse>
		>(endpoints.organizations.list, {
			params,
		});
		const data = unwrapSuccessEnvelope(response.data);
		return data.items.map(mapOrganization);
	},

	getOrganization: async (id: string): Promise<Organization> => {
		const response = await apiClient.get<
			BackendOrganization | SuccessEnvelope<BackendOrganization>
		>(endpoints.organizations.detail(id));
		return mapOrganization(unwrapSuccessEnvelope(response.data));
	},

	createOrganization: async (data: OrganizationSavePayload): Promise<Organization> => {
		const response = await apiClient.post<
			BackendOrganization | SuccessEnvelope<BackendOrganization>
		>(endpoints.organizations.list, data);
		return mapOrganization(unwrapSuccessEnvelope(response.data));
	},

	updateOrganization: async (
		id: string,
		data: Partial<OrganizationSavePayload>
	): Promise<Organization> => {
		const response = await apiClient.patch<
			BackendOrganization | SuccessEnvelope<BackendOrganization>
		>(endpoints.organizations.detail(id), data);
		return mapOrganization(unwrapSuccessEnvelope(response.data));
	},

	toggleOrganization: async (id: string): Promise<Organization> => {
		const response = await apiClient.patch<
			BackendOrganization | SuccessEnvelope<BackendOrganization>
		>(endpoints.organizations.toggle(id));
		return mapOrganization(unwrapSuccessEnvelope(response.data));
	},

	deleteOrganization: async (id: string): Promise<void> => {
		await apiClient.delete(endpoints.organizations.detail(id));
	},
};
